import * as Yup from 'common/yup';
import {includes, isString} from 'lodash';

import {TransportType} from '../TransportType';
import {FileTransferVerifyType} from '../FileTransferVerifyType';
import {
   amazonS3ServerSchema,
   aS2ServerSchema,
   azureStorageServerSchema,
   boxServerSchema,
   cloudServerSchema,
   DropboxServerSchema,
   FtpServerSchema,
   FtpsServerSchema,
   GoogleCloudServerSchema,
   HttpServerSchema,
   HttpsServerSchema,
   LocalServerSchema,
   OracleCloudServerSchema,
   SftpServerSchema,
   ShareFileServerSchema,
   SMBServerSchema,
} from './Server';
import i18n from '../../i18n';
import {FtpLibrary} from '../FtpLibrary';
import {SmbLibrary} from '../SmbLibrary';
import {TransportTypeName} from 'common/enums';
import {transportTypeNameSchema} from 'types/transport/TransportTypeName';
import {
   AbstractTransport,
   fileTransferVerifyTypeSchema,
   ITransportType,
   Transport,
   transportTypeSchema,
} from './Abstract/AbstractTransport';
import {BiscomTransport} from './Biscom/BiscomTransport';
import {CONTEXT_SEND_PARAMETER} from 'common/yup/constants';
import {PostTransactionCommand} from 'types/TransactionCommand';
import {EMAIL_DELIMITER} from 'common/constants';
import {SharepointTransport} from 'types/transport/enums/Sharepoint/SharepointTransport';
import {OneDriveTransport} from './OneDrive/OneDriveTransport';
import {ZohoWorkdriveTransport} from './Zoho/ZohoWorkdriveTransport';

/**
 * AmazonS3 transport
 */
export const amazonS3TransportSchema = AbstractTransport.shape({
   typeName: transportTypeNameSchema.default(TransportTypeName.AMAZONS3),
   server: amazonS3ServerSchema,
   transportType: transportTypeSchema.default(TransportType.enum.AMAZONS3),
});

export type AmazonS3Transport = Yup.InferType<typeof amazonS3TransportSchema>;

/**
 * AS2 transport
 */
export const aS2TransportSchema = AbstractTransport.shape({
   typeName: transportTypeNameSchema.default(TransportTypeName.AS2),
   server: aS2ServerSchema,
   transportType: transportTypeSchema.default(TransportType.enum.AS2),
   fileTransferVerifyType: fileTransferVerifyTypeSchema.default(FileTransferVerifyType.enum.NONE),
});

export type AS2Transport = Yup.InferType<typeof aS2TransportSchema>;

/**
 * AZURE STORAGE transport
 */
export const azureStorageTransportSchema = AbstractTransport.shape({
   typeName: transportTypeNameSchema.default(TransportTypeName.AZURESTORAGE),
   server: azureStorageServerSchema,
   transportType: transportTypeSchema.default(TransportType.enum.AZURE_STORAGE),
   fileTransferVerifyType: fileTransferVerifyTypeSchema.default(FileTransferVerifyType.enum.SIZE),
});

export type AzureStorageTransport = Yup.InferType<typeof azureStorageTransportSchema>;

/**
 * Box transport
 */
export const boxTransportSchema = AbstractTransport.shape({
   typeName: transportTypeNameSchema.default(TransportTypeName.BOX),
   server: boxServerSchema,
   transportType: transportTypeSchema.default(TransportType.enum.BOX),
   fileTransferVerifyType: fileTransferVerifyTypeSchema.default(FileTransferVerifyType.enum.SIZE),
});

export type BoxTransport = Yup.InferType<typeof boxTransportSchema>;

/**
 * Cloud transport
 */
export const cloudTransportSchema = AbstractTransport.shape({
   typeName: transportTypeNameSchema.default(TransportTypeName.REMOTE),
   server: cloudServerSchema,
   postCommand: PostTransactionCommand,
   transportType: transportTypeSchema.default(TransportType.enum.CLOUD),
   fileTransferVerifyType: fileTransferVerifyTypeSchema.default(FileTransferVerifyType.enum.SIZE),
});

export type CloudTransport = Yup.InferType<typeof cloudTransportSchema>;

/**
 * Dropbox transport
 */
export const dropboxTransportSchema = AbstractTransport.shape({
   typeName: transportTypeNameSchema.default(TransportTypeName.DROPBOX),
   server: DropboxServerSchema,
   transportType: transportTypeSchema.default(TransportType.enum.DROPBOX),
   fileTransferVerifyType: fileTransferVerifyTypeSchema.default(FileTransferVerifyType.enum.SIZE),
});

export type DropboxTransport = Yup.InferType<typeof dropboxTransportSchema>;

const INCOMING_SERVER_DFLT = '<ANY>';
const INCOMING_SUBJECT_DFLT = '<ANY>';
/**
 * Email transport
 */
export const emailTransportSchema = AbstractTransport.shape({
   typeName: transportTypeNameSchema.default(TransportTypeName.EMAIL),
   transportType: transportTypeSchema.default(TransportType.enum.EMAIL),
   fileTransferVerifyType: fileTransferVerifyTypeSchema.default(FileTransferVerifyType.enum.NONE),
   /** Outgoing (SMTP) address. Can be a list of addresses separated by semi-colons.*/
   outgoingAddress: Yup.mixed().when(CONTEXT_SEND_PARAMETER, {
      is: true,
      then: Yup.mixed().transform(function (val, origin) {
         if (isString(val)) {
            return val;
         }
         return origin.join(';');
      }),
      otherwise: Yup.originArray()
         // .compact()
         .of(
            Yup.string()
               .email(i18n.t('Recipient Sender Address invalid'))
               .required(i18n.t('Recipient Sender Address must not be blank'))
         )
         .default([''])
         .min(1, i18n.t('Recipient Address field must not be blank'))
         .transform(function (val, original) {
            if (this.isType(val)) {
               return val;
            }
            return original.split(EMAIL_DELIMITER);
         }),
   }),
   /** Outgoing (SMTP) subject. */
   outgoingSubject: Yup.string(),
   /** Outgoing (SMTP) body. */
   outgoingBody: Yup.string(),

   /** Incoming (IMAP, POP3) address.  Can be a list of addresses separated by semi-colons.*/
   incomingAddress: Yup.lazy((val) =>
      !val || val === INCOMING_SERVER_DFLT
         ? Yup.string().default(INCOMING_SERVER_DFLT)
         : Yup.string().email(i18n.t('Receiving Sender Address invalid'))
   ),
   /** Incoming (IMAP, POP3) subject. */
   incomingSubject: Yup.string().default(INCOMING_SUBJECT_DFLT),
}).transform(function (val) {
   delete val.server;
   return val;
});

export interface EmailTransport extends Yup.InferType<typeof emailTransportSchema> {
   outgoingAddress: string[];
   incomingAddress: string;
}

/**
 * FILE_SYSTEM transport
 */
export const localTransportSchema = AbstractTransport.shape({
   typeName: transportTypeNameSchema.default(TransportTypeName.LOCAL),
   transportType: transportTypeSchema.default(TransportType.enum.FILE_SYSTEM),
   server: LocalServerSchema,
   fileTransferVerifyType: fileTransferVerifyTypeSchema.default(FileTransferVerifyType.enum.SIZE),
   retainModifyDate: Yup.bool(),
   retainPermissions: Yup.bool(),
});

export type LocalTransport = Yup.InferType<typeof localTransportSchema>;

/**
 * FTP transport
 */
export const ftpTransportSchema = AbstractTransport.shape({
   typeName: transportTypeNameSchema.default(TransportTypeName.FTP),
   server: FtpServerSchema,
   transportType: transportTypeSchema.default(TransportType.enum.FTP),
   fileTransferVerifyType: fileTransferVerifyTypeSchema.default(FileTransferVerifyType.enum.SIZE),
   retainModifyDate: Yup.bool(),

   allowSelfSignedCertificates: Yup.bool(),
   allowExpiredCertificates: Yup.bool(),

   /** Library used by this transport. */
   library: Yup.string().oneOf(FtpLibrary.keys).default(FtpLibrary.enum.JSCAPE_90),
   /**
    * In Diplomat MFT 9.5, we re-use this deprecated property to indicate modern RSA sign algs
    */
   usePipelining: Yup.boolean().default(true),
   /**
    * True if updated to version 7.0. When updating to version 7.0
    * we change the library choice to be JSCAPE_2015.
    */
   updatedTo70: Yup.boolean().default(true),
   /**
    * True if updated to version 7.2. When updating to version 7.2
    * if the library choice is JSCAPE_2015 we change it to JSCAPE_90.
    */
   updatedTo72: Yup.boolean().default(true),
});

export type FtpTransport = Yup.InferType<typeof ftpTransportSchema>;

/**
 * FTPS (TLS) transport
 */
export const ftpsTransportSchema = ftpTransportSchema.shape({
   server: FtpsServerSchema,
   transportType: transportTypeSchema.default(TransportType.enum.FTPS),
   fileTransferVerifyType: fileTransferVerifyTypeSchema.default(FileTransferVerifyType.enum.SIZE),
});

export type FtpsTransport = Yup.InferType<typeof ftpsTransportSchema>;

/**
 * SFTP transport
 */
export const sftpTransportSchema = ftpTransportSchema.shape({
   server: SftpServerSchema,
   transportType: transportTypeSchema.default(TransportType.enum.SFTP),
   fileTransferVerifyType: fileTransferVerifyTypeSchema.default(FileTransferVerifyType.enum.SIZE),
});

export type SftpTransport = Yup.InferType<typeof sftpTransportSchema>;

/**
 * GOOGLE_CLOUD transport
 */
export const googleCloudTransportSchema = AbstractTransport.shape({
   typeName: transportTypeNameSchema.default(TransportTypeName.GOOGLECLOUD),
   server: GoogleCloudServerSchema,
   transportType: transportTypeSchema.default(TransportType.enum.GOOGLE_CLOUD),
   fileTransferVerifyType: fileTransferVerifyTypeSchema.default(FileTransferVerifyType.enum.SIZE),
});

export type GoogleCloudTransport = Yup.InferType<typeof googleCloudTransportSchema>;

/**
 * HTTP transport
 */
export const httpTransportSchema = AbstractTransport.shape({
   typeName: transportTypeNameSchema.default(TransportTypeName.HTTP),
   server: HttpServerSchema,
   transportType: transportTypeSchema.default(TransportType.enum.HTTP),
   fileTransferVerifyType: fileTransferVerifyTypeSchema.default(FileTransferVerifyType.enum.SIZE),

   /** Does this transport allow self-signed certificates? */
   allowSelfSignedCertificates: Yup.boolean(),
   /** Does this transport allow expired certificates? */
   allowExpiredCertificates: Yup.boolean(),
});

export type HttpTransport = Yup.InferType<typeof httpTransportSchema>;

/**
 * HTTPS transport
 */
export const httpsTransportSchema = httpTransportSchema.shape({
   server: HttpsServerSchema,
   transportType: transportTypeSchema.default(TransportType.enum.HTTPS),
   fileTransferVerifyType: fileTransferVerifyTypeSchema.default(FileTransferVerifyType.enum.SIZE),
});

export type HttpsTransport = Yup.InferType<typeof httpsTransportSchema>;

/**
 * ORACLE_CLOUD transport
 */
export const oracleCloudTransportSchema = AbstractTransport.shape({
   typeName: transportTypeNameSchema.default(TransportTypeName.ORACLECLOUD),
   server: OracleCloudServerSchema,
   transportType: transportTypeSchema.default(TransportType.enum.ORACLE_CLOUD),
   fileTransferVerifyType: fileTransferVerifyTypeSchema.default(FileTransferVerifyType.enum.SIZE),
});

export type OracleCloudTransport = Yup.InferType<typeof oracleCloudTransportSchema>;

/**
 * ShareFile transport
 */
export const shareFileTransportSchema = AbstractTransport.shape({
   typeName: transportTypeNameSchema.default(TransportTypeName.SHAREFILE),
   server: ShareFileServerSchema,
   transportType: transportTypeSchema.default(TransportType.enum.SHAREFILE),
   fileTransferVerifyType: fileTransferVerifyTypeSchema.default(FileTransferVerifyType.enum.SIZE),
});

export type ShareFileTransport = Yup.InferType<typeof shareFileTransportSchema>;

/**
 * SMB transport
 */
export const smbTransportSchema = AbstractTransport.shape({
   typeName: transportTypeNameSchema.default(TransportTypeName.SMB),
   server: SMBServerSchema,
   transportType: transportTypeSchema.default(TransportType.enum.SMB),
   fileTransferVerifyType: fileTransferVerifyTypeSchema.default(FileTransferVerifyType.enum.SIZE),

   library: Yup.string().oneOf(SmbLibrary.keys).default(SmbLibrary.enum.JCIFS),
   retainModifyDate: Yup.boolean(),
});

export type SMBTransport = Yup.InferType<typeof smbTransportSchema>;

export const getTransportSchemaByType = (
   transportType: Transport['transportType']
): typeof AbstractTransport => {
   // console.debug(type);
   switch (transportType) {
      case TransportType.enum.AMAZONS3:
         return amazonS3TransportSchema;
      case TransportType.enum.AS2:
         return aS2TransportSchema;
      case TransportType.enum.BISCOM:
         return BiscomTransport;
      case TransportType.enum.AZURE_STORAGE:
         return azureStorageTransportSchema;
      case TransportType.enum.BOX:
         return boxTransportSchema;
      case TransportType.enum.CLOUD:
         return cloudTransportSchema;
      case TransportType.enum.DROPBOX:
         return dropboxTransportSchema;
      case TransportType.enum.EMAIL:
         return emailTransportSchema;
      case TransportType.enum.FTP:
         return ftpTransportSchema;
      case TransportType.enum.FTPS:
         return ftpsTransportSchema;
      case TransportType.enum.GOOGLE_CLOUD:
         return googleCloudTransportSchema;
      case TransportType.enum.HTTP:
         return httpTransportSchema;
      case TransportType.enum.HTTPS:
         return httpsTransportSchema;
      case TransportType.enum.ORACLE_CLOUD:
         return oracleCloudTransportSchema;
      case TransportType.enum.SFTP:
         return sftpTransportSchema;
      case TransportType.enum.SHAREFILE:
         return shareFileTransportSchema;
      case TransportType.enum.SMB:
         return smbTransportSchema;
      case TransportType.enum.SHAREPOINT:
         return SharepointTransport;
      case TransportType.enum.ONEDRIVE:
         return OneDriveTransport;
      case TransportType.enum.WORKDRIVE:
         return ZohoWorkdriveTransport;
      case TransportType.enum.FILE_SYSTEM:
      default:
         return localTransportSchema;
   }
};

export const isHttp = (val: Transport) =>
   includes([TransportType.enum.HTTP, TransportType.enum.HTTPS], val.transportType);
export const isEmail = (val: Transport) => TransportType.enum.EMAIL === val.transportType;
export const isEmailTransportType = (val: ITransportType) => TransportType.enum.EMAIL === val;
